import { lazy, Suspense } from 'react';
import { Faction, PLAYER_DISTRIBUTION } from '@secretnenshi/shared';
import { DisclaimerText } from '../components/chrome';
import { Page, Section } from '../components/ui';
import { go } from '../state/actions';
import type { InfoPage } from '../state/store';
import { useTheme } from '../theme/useTheme';

const TITLES: Record<InfoPage, string> = {
  'how-to-play': 'How to play',
  characters: 'Characters',
  sources: 'Sources',
  about: 'About',
  disclaimer: 'Satire disclaimer',
};

export function Info({ page }: { page: InfoPage }) {
  return (
    <Page title={TITLES[page]} onBack={() => go({ name: 'landing' })}>
      {page === 'how-to-play' ? <HowToPlay /> : null}
      {page === 'characters' ? <Characters /> : null}
      {page === 'sources' ? <Sources /> : null}
      {page === 'about' ? <About /> : null}
      {page === 'disclaimer' ? <DisclaimerText /> : null}
    </Page>
  );
}

/**
 * The variant's own story, then the rules. The rules are the same mechanics
 * for every variant, written with the variant's words and rule settings.
 */
function HowToPlay() {
  const th = useTheme();
  const t = th.t;
  const rules = th.rules(7);
  const maj = th.factionPlural(Faction.SEPARATIST);
  const min = th.factionPlural(Faction.FEDERALIST);
  const leader = t.leader.name;
  const { minister, premier } = th;
  return (
    <div className="prose">
      <h2 className="section-label">{t.openingHeading}</h2>
      {t.opening.map((line, i) =>
        line.startsWith('!') ? (
          <p key={i} className="shout">
            {line.slice(1)}
          </p>
        ) : (
          <p key={i}>{line}</p>
        ),
      )}

      <Section label="Each round">
        <ol>
          <li>
            The <strong>{minister}</strong> rotates around the table and nominates a <strong>{premier}</strong>.
          </li>
          <li>
            Everyone votes <strong>{t.phases.confidenceVote.confidence}</strong> or <strong>{t.phases.confidenceVote.noConfidence}</strong>.
            Ballots are secret and ties fail.
          </li>
          <li>
            If the government stands, the {minister} draws {rules.legislativeDraw} policies and discards one; the {premier} enacts one of the
            other two.
          </li>
          <li>
            <strong>{t.phases.speech.name}:</strong> the {minister} and {premier} say what they received and discarded. Their speeches are
            revealed together. <strong>Nobody has to tell the truth.</strong>
          </li>
          <li>Any executive power the {min} earned is used by the {minister}.</li>
          <li>
            <strong>{t.phases.questionPeriod.name}:</strong> open debate, up to the time the host set. It ends early when a majority votes to
            end debate.
          </li>
          <li>
            {rules.gridlockLimit} failed governments in a row cause <strong>{t.phases.gridlock.name}</strong>: the top policy is enacted
            automatically.
          </li>
        </ol>
      </Section>
      <Section label={`Who can be ${premier}`}>
        <p>The {minister} can’t nominate themself.</p>
        <p>
          <strong>Term limits:</strong> the {minister} and {premier} of the last <em>approved</em> government can’t be nominated for the next{' '}
          {premier}. A government that loses its vote doesn’t count.
        </p>
        <p>
          With {rules.smallTableActivePlayers} or fewer players still in the game, only the last {premier} is barred; the last {minister} may
          serve again. {t.phases.gridlock.name} clears all term limits.
        </p>
      </Section>
      <Section label="Executive powers">
        <p>Some spaces on the {t.policies.FEDERALIST.short} track give the {minister} a power when that policy is enacted:</p>
        <ul className="plain">
          <li>
            <strong>{t.powers.CAMPAIGN_RESEARCH.name}:</strong> privately learn one player’s faction.
          </li>
          <li>
            <strong>{t.powers.INTERNAL_POLLING.name}:</strong> privately see the next three policies.
          </li>
          <li>
            <strong>{t.powers.UNITY_GOVERNMENT.name}:</strong> choose the next {minister}.
          </li>
          <li>
            <strong>{t.powers.COURT_CHALLENGE.name}:</strong> remove a player from the game.
          </li>
          <li>
            <strong>{t.powers.VETO.name}:</strong> late in the game, the {premier} may propose discarding both policies.
          </li>
        </ul>
      </Section>
      <Section label="Winning">
        <p>
          <strong>{maj}</strong> win with {rules.separationPoliciesToWin} {t.policies.SEPARATION.name.replace(/y$/, 'ies')}, or by removing{' '}
          {leader} through a {t.powers.COURT_CHALLENGE.name}.
        </p>
        <p>
          <strong>{min}</strong> win with {rules.federalistPoliciesToWin} {t.policies.FEDERALIST.name.replace(/y$/, 'ies')}, or by electing{' '}
          {leader} {premier} once {rules.nenshiThreshold} {t.policies.FEDERALIST.short} policies are in place.
        </p>
      </Section>
      <Section label="Who knows what">
        <p>
          {maj} are the majority but don’t know each other. {min} know one another and know who {leader} is.
        </p>
        <p>
          In games of 5–6, {leader} also knows the {min}. In larger games, {leader} plays alone.
        </p>
        <table className="table">
          <thead>
            <tr>
              <th>Players</th>
              <th>{maj}</th>
              <th>{min}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(PLAYER_DISTRIBUTION).map(([n, d]) => (
              <tr key={n}>
                <td>{n}</td>
                <td>{d.separatists}</td>
                <td>
                  {d.ordinaryFederalists} + {leader}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section label="Keep your phone to yourself">
        <p>
          Your screen looks the same whatever your role. Press and hold <strong>My identity</strong> to see yours; let go and it disappears.
        </p>
      </Section>
    </div>
  );
}

const RosterGallery = lazy(() => import('../game/RosterGallery'));

/** Pre-game only: no roles are assigned here (spec §48). */
function Characters() {
  return (
    <div className="prose">
      <p>Which characters appear depends on the number of players. Which player receives which character is random.</p>
      <Suspense fallback={<p className="hint">Loading…</p>}>
        <RosterGallery />
      </Suspense>
    </div>
  );
}

function Sources() {
  const th = useTheme();
  const real = th.theme.characters.filter((c) => !c.fictional);
  return (
    <div className="prose">
      <DisclaimerText />
      <Section label="Character sources">
        {real.length === 0 ? <p className="hint">This game’s characters are fictional.</p> : null}
        {real.map((c) => (
          <div key={c.id} className="source-entry">
            <h3>{c.displayName}</h3>
            {c.sources.length ? (
              <ul className="plain">
                {c.sources.map((s) => (
                  <li key={s.url}>
                    <a href={s.url} target="_blank" rel="noreferrer noopener">
                      {s.title}
                    </a>{' '}
                    — {s.publisher}
                    {s.publicationDate ? `, ${s.publicationDate}` : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="hint">Sources pending review.</p>
            )}
          </div>
        ))}
      </Section>
    </div>
  );
}

function About() {
  const th = useTheme();
  return (
    <div className="prose">
      {th.t.about.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
      <DisclaimerText />
    </div>
  );
}
